import { localDateString } from './dailyDate';

/**
 * Local-storage gate for daily modes. "Did the user already play
 * today's puzzle? If so, what was their result?" Pure local for v1
 * — when the backend lands, swap the storage layer for an API call
 * and the rest of the daily flow is untouched.
 */

const STORAGE_PREFIX = 'atlasguess.daily.';

/**
 * One round of a city-distance daily — what we need to redisplay the
 * end-screen result deterministically without rerunning the round.
 */
export interface CityDailyRoundResult {
  cityId: string;
  distanceKm: number;
}

/** Stored shape — the date stamp lets us invalidate the entry when a new day starts. */
export interface CityDailyResult {
  date: string; // YYYY-MM-DD local
  modeId: string;
  rounds: CityDailyRoundResult[];
}

function storageKey(modeId: string): string {
  return `${STORAGE_PREFIX}${modeId}`;
}

/**
 * Read the user's stored result for today, if any. Returns null if
 * nothing stored, the storage entry is malformed, or the date stamp
 * is from a previous day (stale result — caller treats as "not yet
 * played today").
 */
export function getTodaysCityDailyResult(modeId: string): CityDailyResult | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(storageKey(modeId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CityDailyResult;
    if (parsed.date !== localDateString()) return null;
    if (parsed.modeId !== modeId) return null;
    if (!Array.isArray(parsed.rounds)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Persist today's result so a refresh / revisit shows the end screen, not a fresh run. */
export function saveCityDailyResult(modeId: string, rounds: CityDailyRoundResult[]): void {
  if (typeof window === 'undefined') return;
  const result: CityDailyResult = {
    date: localDateString(),
    modeId,
    rounds,
  };
  try {
    window.localStorage.setItem(storageKey(modeId), JSON.stringify(result));
  } catch {
    // Quota exceeded / private mode — fail silently. The user just
    // loses persistence; the active session still works.
  }
}
